/* ---------------------------------------------------------------------------
   Ready Cart BD — product-page behaviour.

   There is ONE product template now, `rcb-landing`. There were two, and this
   file exists because of that: `rcb-simple-product` carried a near-identical
   inline copy of the CTA scrolling and the variant sync, and two copies of one
   behaviour in two sections is how a long page and a short page quietly start
   behaving differently. The short template is gone and the duplication risk
   with it, but the file stays — a cached static asset beats inline script on a
   page the ad budget pays for.

   Everything is read from the DOM. No Liquid is interpolated into this file,
   which is what lets it be a static asset:

     [data-rcb-root]            the section wrapper
     [data-rcb-scroll]          any CTA that should scroll to the buy box
     [data-rcb-track]/[-dot]    hero carousel + its dots
     [data-rcb-jump="n"]        detail-grid tile → hero slide n
     [data-rcb-id-input]        hidden cart id — the product's only variant
     [data-rcb-section]         this section's id, for re-rendering it
     [data-rcb-buy]             the buy box — REPLACED after every cart write
       [data-rcb-line-key]        a cart line's key. On the product's own
                                  quantity row, and on each accessory already
                                  taken. Anything inside it WRITES to the cart
       [data-rcb-remove]          takes that line out; a real link underneath
       [data-rcb-step-input]      quantity, [data-rcb-step-by] its ± buttons
       [data-rcb-buy-now]         add, then go to the cart
       [data-rcb-add]             add, then stay here
       [data-rcb-added]           the inline confirmation — THIS product only
     [data-rcb-attach-region]   suggestions wrapper — REPLACED, always present
     [data-rcb-attach-form]     one suggested accessory; a real /cart/add form

   ⚠ Both replaced regions are re-rendered by Shopify and pasted in, so NOTHING
   may be bound to an element inside them. Every listener here is delegated
   from `[data-rcb-root]`, which is outside both.

   ---------------------------------------------------------------------------
   What this file no longer does, and why that is the headline.

   It used to run an order card: the Shopify cart mirrored onto the product
   page, written back on a 350ms debounce, reconciled against `/cart.js` at
   boot, with its own money formatter and a probe proving that formatter
   matched the store's. Roughly 250 lines, and the failure modes were silent.

   All of it existed to work around one thing — the COD popup of the time could
   not edit what it was ordering, so quantity and accessories had to be settled
   before it opened. The card settled them by becoming a second cart, and two carts
   cannot be kept in sync. They visibly were not.

   The order form lives on the cart page and only there. So the product page
   has no form to feed and no total to quote: it
   puts lines in the Shopify cart, and the cart page — one surface, one total —
   takes the order. This file writes to the cart through the same endpoint any
   Shopify theme uses, and formats no money at all.

   Two things went with it that were genuinely load-bearing, and are not
   missed for a reason worth recording. The `reconcile()` pass cleared foreign
   lines out of the cart because a leaked line would be silently charged by a
   popup the visitor never saw itemised; that is not a leak now, it is a cart
   line on a cart page the visitor reads before ordering. And the geometric
   probe that hid our button whenever a third-party one was injected is gone
   with the injection — nothing foreign renders here at all.

   ---------------------------------------------------------------------------
   Events dispatched on `document`, for pixels to be wired to without editing
   this file. Values come from real product data, never hard-coded:

     rcb:add      { variantId, productId, quantity, value, currency, buyNow }
     rcb:attach   { variantId, productId, quantity, value, currency }

   `value` is a UNIT price in both, with `quantity` beside it. Multiplying them
   is the pixel wiring's job; this file formats no money and does no arithmetic.

   Both are candidates for Meta's `AddToCart`. `rcb:add` with `buyNow: true` is
   also the closest thing this site has to an intent-to-order signal on a
   product page, which matters while the pixel is being trained on
   InitiateCheckout rather than Purchase.
   --------------------------------------------------------------------------- */

(() => {
  const roots = document.querySelectorAll('[data-rcb-root]');

  /* ---- talking to the Shopify cart ----
     One helper for every write: the buy box's add, a suggestion's add, and a
     parcel line's quantity change. `/cart/add.js` ADDS to a line rather than
     setting it, which is what both adds want — tapping "সাথে নিন" twice on the
     same accessory means two of them — while `/cart/change.js` SETS, which is
     what a stepper on an existing line means.

     Every caller passes `sections`, so Shopify returns freshly rendered markup
     for this section alongside the write. One round trip, and the page is
     repainted from the server rather than from anything this file believes.

     Nothing here talks to the order form, and nothing needs to — it reads the
     cart for itself when its own page loads. */
  const cartWrite = async (endpoint, body) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(endpoint + ' failed');
    return response.json();
  };

  /* ---- keeping the header badge honest ----
     This was the visible half of the old desync: the badge is rendered by
     Liquid at page load and never moved again, so a page that had written to
     the cart showed a count belonging to some earlier state.

     Horizon's own `cart-icon.js` already listens for `shopify:cart:lines-update`
     and re-renders from `event.promise`, so the fix is to speak its protocol
     rather than to reach into its DOM. Using the theme's event instead of
     setting `.cart-bubble__text-count` by hand means the drawer, the count and
     anything else Horizon adds later all stay correct for free — and nothing
     breaks if Horizon renames a class.

     The item count is asked of `/cart.js` rather than inferred, because a
     `/cart/add.js` response describes the line it just added, not the cart. */
  const announceCart = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();

      const event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      event.promise = Promise.resolve({ cart: null, detail: { itemCount: cart.item_count } });
      document.dispatchEvent(event);

      return cart;
    } catch (error) {
      /* A badge that did not move is cosmetic. The cart itself is correct — the
         add already succeeded — and the cart page will render the truth. Never
         surface this to the visitor. */
    }
  };

  roots.forEach((root) => {
    /* ---- the CTAs scroll ----
       Every CTA aims at the buy box. They scroll rather than pressing anything
       for the visitor: a CTA that ordered on their behalf would book quantity
       1 of the default variant, and the stepper and the accessory rows are
       exactly what sits past the point a CTA lands.

       Scrolled rather than jumped so the visitor keeps their sense of place; a
       jump on a long page reads as a new page having loaded. */
    const buyBox = root.querySelector('[data-rcb-buy]');

    /* ---- keeping the card true after a cart write ----

       Every write asks Shopify to re-render THIS section and pastes the fresh
       markup back into two regions. Nothing here builds a line, and nothing
       decides what a line says — Liquid does, on the server, from the cart
       itself. That is what lets the parcel list carry working quantity
       controls without this page becoming a second cart: it holds no state to
       drift, and the money on it was formatted by the store's own filter.

       Same mechanism and the same reasoning as `rcb-cart.js`.

       Two regions, and both are needed — they are the two halves of one
       `rcb.attach` resolution and a cart write moves a row from one to the
       other, so repainting either alone would print the same accessory twice
       or lose it entirely:

         [data-rcb-buy]            the product row and its stepper, the
                                   accessories already taken, the buttons and
                                   the confirmation panel — all of which change
                                   when the cart does
         [data-rcb-attach-region]  what is still on offer. A suggestion the cart
                                   holds is not offered here, so removing a line
                                   has to make it offerable again. The wrapper is
                                   rendered unconditionally by the section
                                   precisely so there is always a node to swap
                                   into.

       ⚠ Everything inside those regions is replaced, so nothing may be bound
       to an element inside them. Every listener below is delegated from
       `root`, which is outside both. */
    const REGIONS = ['[data-rcb-buy]', '[data-rcb-attach-region]'];

    /* One write per line at a time. Two taps on `−` a moment apart would
       otherwise race, and the loser's answer — rendered from a cart that has
       moved on — would be the one pasted in. */
    const busyLines = new Set();

    /* ⚠ `sections_url` IS NOT OPTIONAL, and leaving it off fails silently.

       A cart write is a POST to `/cart/change.js`, so without it Shopify
       renders this section with no page context — `product` is nil, the buy box
       renders nothing at all, and the swap below finds no `[data-rcb-buy]` to
       copy. The card is then left showing the cart as it was before the tap,
       which is the exact disagreement this whole mechanism exists to prevent.
       Measured: 14.9KB of section without it, 35.9KB with.

       Every write goes through this, so no call site can forget it. */
    const sectionParams = () => ({
      sections: root.dataset.rcbSection,
      sections_url: window.location.pathname,
    });

    /* Steppers ship their − and + hidden, because they are the half that
       cannot work without this file. Fresh markup means fresh hidden ones, so
       this runs at load and again after every repaint. */
    const revealSteppers = () => {
      root.querySelectorAll('[data-rcb-step-by]').forEach((b) => { b.hidden = false; });
    };

    const repaint = (sections) => {
      const html = sections && sections[root.dataset.rcbSection];
      if (!html) return false;

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const source = fresh.querySelector('[data-rcb-root]');
      if (!source) return false;

      REGIONS.forEach((selector) => {
        const from = source.querySelector(selector);
        const to = root.querySelector(selector);
        if (from && to) to.innerHTML = from.innerHTML;
      });

      revealSteppers();
      return true;
    };

    /* Reveal the buy box's confirmation, and give it the page's one action.

       ⚠ THE PANEL IS A STATEMENT ABOUT THIS PRODUCT — "the saree is in your
       cart, go and finish" — so ONLY this product going into the cart may
       reveal it. It used to take a `retire` flag and the accessory rows called
       it with `retire: false`, on the reasoning that an accessory added alone
       still needs a route on to the cart. That was wrong in the one case it
       most mattered: tap `যোগ করুন` on the earrings without ever adding the
       saree, and a panel reading `কার্টে যোগ হয়েছে · অর্ডার সম্পূর্ণ করুন`
       appeared directly under the saree's own buttons. It is inches from the
       saree's price, so it reads as the saree — and its button led to a cart
       holding one ৳120 accessory and a ৳120 delivery charge.

       An accessory-only add now confirms on the control that was pressed and
       nowhere else. There is no dead end: `cart_type` is `page`, so the header
       cart icon is a plain link to /cart, and the saree's own buttons stay the
       one primary action on the page — which is also the outcome the shop
       wants, since the attach only pays when it rides an existing parcel.

       Revealing hides every `data-rcb-precart` control, which is now exactly
       one thing: the pair of add buttons. Both of them ADD, so beside a line
       that already exists they are a trap — `এখনই অর্ডার করুন` reads as "yes,
       order it" to someone who has already tapped add, and what it does is put
       a second saree in the cart and leave for /cart.

       ⚠ The stepper is NOT hidden with them any more. It was, back when a
       `কার্টে আছে` list above it carried a second stepper for the same line;
       that list is gone, so the product's own row is the only place its
       quantity can be changed and it stays — switched by Liquid from composing
       an add to editing the line it became.

       Idempotent, and safe to call on a panel Liquid already revealed —
       `rcb-buybox` ships it open when the cart ALREADY holds this product, so
       the two paths agree. */
    function revealAdded() {
      if (!buyBox) return;
      const added = buyBox.querySelector('[data-rcb-added]');
      if (added) added.hidden = false;
      buyBox.querySelectorAll('[data-rcb-precart]').forEach((el) => { el.hidden = true; });
    }

    root.querySelectorAll('[data-rcb-scroll]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const id = (link.getAttribute('href') || '').slice(1);
        const target = (id && root.querySelector('#' + id)) || buyBox;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    /* ---- hero carousel ----
       Slides are whatever the product's media is, photos and video alike, so
       nothing here may assume an <img>. */
    const track = root.querySelector('[data-rcb-track]');
    const dots = [...root.querySelectorAll('[data-rcb-dot]')];
    const thumbs = [...root.querySelectorAll('[data-rcb-thumbs] [data-rcb-jump]')];
    const slides = track ? [...track.children] : [];
    const prevBtn = root.querySelector('[data-rcb-prev]');
    const nextBtn = root.querySelector('[data-rcb-next]');
    let activeIndex = 0;

    const showSlide = (index) => {
      const slide = slides[index];
      if (slide && track) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    };

    if (track && slides.length) {
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          activeIndex = Number(dot.dataset.rcbDot);
          showSlide(activeIndex);
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          activeIndex = (activeIndex - 1 + slides.length) % slides.length;
          showSlide(activeIndex);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          activeIndex = (activeIndex + 1) % slides.length;
          showSlide(activeIndex);
        });
      }

      /* Observed against the track, not the viewport, so the active dot stays
         correct however far down the page the hero has scrolled. */
      const seen = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = slides.indexOf(entry.target);

            /* A video swiped past keeps playing — and keeps talking — behind
               whatever the visitor moved on to. Stop it when its slide leaves,
               never start one: playback is the visitor's decision. */
            if (!entry.isIntersecting) {
              entry.target.querySelectorAll('video').forEach((video) => {
                if (!video.paused) video.pause();
              });
              return;
            }

            if (index > -1) {
              activeIndex = index;
              dots.forEach((dot, i) => dot.classList.toggle('is-on', i === index));
              thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
            }
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((slide) => seen.observe(slide));
    }

    /* ---- detail grid tiles jump back to the hero ----
       The tiles are anchors to the hero, so a tap already lands in the right
       place with the script blocked. All this adds is putting the carousel on
       the item that was tapped — the reason the visitor tapped it. */
    root.querySelectorAll('[data-rcb-jump]').forEach((tile) => {
      tile.addEventListener('click', (event) => {
        const index = Number(tile.dataset.rcbJump);
        if (!slides[index]) return;

        event.preventDefault();
        showSlide(index);
        /* The rail sits beside the photo, so the page must not move for it. */
        if (track && !tile.closest('[data-rcb-thumbs]')) track.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    /* ---- variants ----
       A variant-selection block sat here: it read a Liquid-rendered table of
       every variant's id, price, compare-at and availability, rewrote the
       form's id when a control changed, repainted the price row from the
       pre-formatted strings, and disabled both buttons on a sold-out one.

       Removed with the rest of the variant machinery, on request. Nothing on
       this site selects a variant now — every product sells its default, which
       Liquid writes straight into the hidden `data-rcb-id-input` field — so
       there was nothing to sync and nothing to repaint.

       The rule it enforced still stands and is now enforced by there being no
       code at all: THIS FILE DOES NO MONEY ARITHMETIC AND FORMATS NO PRICES.
       Every number a visitor sees was rendered by Liquid through the store's
       own `money` filter. If a selector ever comes back, it comes back with its
       Liquid price table; neither half is any use alone. */


    /* ---- steppers ----
       Every stepper on the page: the product's own, one per accessory already
       taken, and one per suggestion still on offer. The − and + ship hidden and
       are revealed here, because the number input beside them already works
       without JavaScript and they do not — the failure mode is a plainer
       control, never a dead one.

       ⚠ DELEGATED, because everything inside `[data-rcb-buy]` and the
       suggestions region is replaced wholesale after every cart write. A
       listener bound to a button is thrown away by the first repaint, and the
       controls go quietly dead. `root` is outside both regions and survives.

       Two kinds of stepper, told apart by `[data-rcb-line-key]` on the wrapper:

         no key    a form field. Typing a number changes what will be ADDED, and
                   nothing happens until the form is submitted.
         key       a line that exists. Changing it WRITES to the cart.

       They are deliberately not the same thing, and the key is what says so. */
    revealSteppers();

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rcb-step-by]');
      if (!button || !root.contains(button)) return;

      const stepper = button.closest('[data-rcb-step]');
      const input = stepper && stepper.querySelector('[data-rcb-step-input]');
      if (!input) return;

      const min = Number(input.min) || 0;
      const max = Number(input.max) || 20;
      const next = Number(input.value) + Number(button.dataset.rcbStepBy);
      const wanted = Math.min(max, Math.max(min, next));
      if (wanted === Number(input.value)) return;

      const holder = stepper.closest('[data-rcb-line-key]');
      if (!holder) { input.value = String(wanted); return; }

      /* A line that exists. The input is NOT written here — the number the
         visitor sees has to come back from the cart, or the page would be
         asserting a quantity Shopify has not agreed to yet. */
      setLineQuantity(holder.dataset.rcbLineKey, wanted, stepper);
    });

    /* ---- remove ----
       The bin on the product row and on each accessory already taken.

       It is a real `<a href="{{ line.url_to_remove }}">` underneath, so it works
       with this file deleted — Shopify handles the GET and redirects back here.
       All this adds is doing it in place, so someone taking earrings back out is
       not thrown off the saree page they were still reading.

       ⚠ Delegated from `root` like everything else, because both regions are
       replaced wholesale after every cart write. The key comes off the same
       `[data-rcb-line-key]` wrapper the stepper reads, so one Liquid value
       drives both controls on a row; without it we fall through to the link,
       which is the correct outcome rather than a dead tap. */
    root.addEventListener('click', (event) => {
      const control = event.target.closest('[data-rcb-remove]');
      if (!control || !root.contains(control)) return;

      const holder = control.closest('[data-rcb-line-key]');
      const key = holder && holder.dataset.rcbLineKey;
      if (!key) return;

      event.preventDefault();
      setLineQuantity(key, 0, holder.querySelector('[data-rcb-step]'));
    });

    /* Set one cart line's quantity, then repaint from the server's own render.

       ⚠ Zero is a removal and only the bin reaches it. Every stepper that
       writes stops at `min="1"`, so nudging a quantity down cannot walk a line
       out of the order by accident — the same rule the cart page follows, and
       it applies here for the same reason: this card's next button asks for
       cash at the door. Undo is a separate, labelled gesture. */
    const setLineQuantity = async (key, quantity, stepper) => {
      if (!key || busyLines.has(key)) return;
      busyLines.add(key);
      if (stepper) stepper.classList.add('is-busy');

      try {
        const data = await cartWrite('/cart/change.js', {
          id: key,
          quantity,
          ...sectionParams(),
        });

        if (!repaint(data.sections)) throw new Error('no section markup');

        /* Straight off the response, so the header badge and the card agree
           about the same moment. `/cart/change.js` answers with the whole cart,
           which `/cart/add.js` does not — hence the extra read there. */
        const event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
        event.promise = Promise.resolve({ cart: null, detail: { itemCount: data.item_count } });
        document.dispatchEvent(event);
      } catch (error) {
        /* Anything at all — network, a line that vanished under us, markup we
           did not expect. Reload, which costs a moment and lands on the truth.
           Leaving a stepper showing a number the cart never accepted is the one
           outcome this whole design exists to prevent. */
        window.location.reload();
      } finally {
        busyLines.delete(key);
        if (stepper) stepper.classList.remove('is-busy');
      }
    };

    /* ---- the buy box ---- */
    if (buyBox) {
      const form = buyBox.closest('form');

      /* Which button was pressed. `event.submitter` gives it directly in every
         browser this shop's traffic actually uses, but a click listener is kept
         as the fallback rather than defaulting to one behaviour or the other:
         guessing wrong either strands a buy-now visitor on the product page or
         teleports an add-to-cart visitor away from a page they were still
         reading.

         Delegated for the same reason as the steppers: the buttons themselves
         are replaced by every repaint. */
      let pressed = null;
      root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-rcb-submit]');
        if (button && root.contains(button)) pressed = button;
      });

      if (form) {
        form.addEventListener('submit', async (event) => {
          const button = event.submitter || pressed;
          if (!button) return;

          /* Only intercept once we are sure we can do the whole job. Anything
             unexpected falls through to the real form post, which works. */
          event.preventDefault();
          pressed = null;

          const buyNow = button.hasAttribute('data-rcb-buy-now');
          /* Read at submit, not held: the stepper is replaced by every repaint,
             so a reference taken at load would point at a detached node. */
          const qtyInput = buyBox.querySelector('[data-rcb-step-input]');
          const quantity = Math.max(1, Math.floor(Number(qtyInput && qtyInput.value) || 1));
          /* The product's only variant id, written by Liquid. Nothing changes
             it any more; if it is somehow absent, fall through to the real form
             post rather than guessing. */
          const idInput = root.querySelector('[data-rcb-id-input]');
          const variantId = idInput && idInput.value;
          if (!variantId) { form.submit(); return; }

          buyBox.classList.add('is-busy');

          try {
            const data = await cartWrite('/cart/add.js', {
              items: [{ id: Number(variantId), quantity }],
              ...sectionParams(),
            });

            document.dispatchEvent(new CustomEvent('rcb:add', {
              detail: {
                variantId: String(variantId),
                productId: root.dataset.rcbPid || '',
                quantity,
                value: root.dataset.rcbValue || '',
                currency: root.dataset.rcbCurrency || '',
                buyNow,
              },
            }));

            /* Buy-now leaves before the badge matters; the cart page renders
               its own count from Liquid. Announcing first would cost a round
               trip on the path that is meant to be the fast one. */
            if (buyNow) {
              window.location.href = '/cart';
              return;
            }

            /* ⚠ THE REPAINT IS NOT OPTIONAL HERE, AND ITS RESULT IS CHECKED.

               The fresh markup is what switches the product's row from
               composing an add to editing the line it became: the label goes,
               the stepper picks up the line key, the bin appears. `revealAdded`
               does none of that — it only unhides the panel and retires the two
               add buttons.

               So a silently-failed repaint left the worst possible pairing on
               screen: a panel reading `কার্টে যোগ হয়েছে` directly under a row
               still reading `কয়টি নেবেন`, with a stepper that no longer had a
               button to submit it and did not yet write to the cart. Dead
               control, contradicted claim, on the card that asks for a cash
               order.

               Reloading costs a moment and lands on the truth — the same policy
               `setLineQuantity` takes below, and for the same reason. */
            if (!repaint(data.sections)) { window.location.reload(); return; }
            revealAdded();
            await announceCart();
          } catch (error) {
            /* The add did not land, so fall back to the real form post rather
               than telling the visitor something happened. `form.submit()`
               does not re-fire this handler, so there is no loop — and the
               visitor ends up on /cart with the item added, which is where
               buy-now was going anyway. */
            form.submit();
            return;
          } finally {
            buyBox.classList.remove('is-busy');
          }
        });
      }
    }

    /* ---- suggested accessories ----
       Each row is its own `<form action="/cart/add">`, so it already works with
       this file deleted — it posts and lands the visitor on /cart. All this
       adds is doing it in place, so someone adding a pair of earrings is not
       thrown off the saree page they were still reading.

       That is the opposite of the old order card's rule, which shipped its
       suggestion rows hidden because they were dead without JavaScript. Real
       forms mean they can ship visible, which is worth more than it sounds:
       the attach sale is the shop's whole accessories margin, and it used to
       be lost entirely on any visit where the script failed.

       An offered row has no stepper — it posts a hidden `quantity` of 1, and
       the stepper appears in the buy box on the line it becomes. The lookup
       below is kept rather than replaced with a literal 1 for two reasons: it
       reads the form's own field wherever that field is, and it means one
       theme setting reinstating a stepper does not silently start charging for
       1 while showing a 3.

       ⚠ Delegated from `root`, like everything else on this page, because the
       whole suggestions region is replaced after every cart write. `submit`
       bubbles, so one listener covers rows that do not exist yet. */
    root.addEventListener('submit', async (event) => {
      const attachForm = event.target.closest('[data-rcb-attach-form]');
      if (!attachForm || !root.contains(attachForm)) return;

      event.preventDefault();

      const variantId = attachForm.dataset.rcbVariant;
      if (!variantId) { attachForm.submit(); return; }

      /* The row's own `name="quantity"` field, hidden and 1 today. Floored and
         integer-ised the same way the buy box does it: a typed `0` or `2.5` in
         a number input is the visitor's, and /cart/add would take it literally
         if a visible control ever comes back here. */
      const rowQty = attachForm.querySelector('[data-rcb-step-input], input[name="quantity"]');
      const quantity = Math.max(1, Math.floor(Number(rowQty && rowQty.value) || 1));

      attachForm.classList.add('is-busy');

      try {
        const data = await cartWrite('/cart/add.js', {
          items: [{ id: Number(variantId), quantity }],
          ...sectionParams(),
        });

        /* `value` is the accessory's UNIT price, with `quantity` beside it.
           Multiplying them out is a pixel wiring's job — this file formats no
           money and does no money arithmetic. Same shape as `rcb:add`. */
        document.dispatchEvent(new CustomEvent('rcb:attach', {
          detail: {
            variantId: String(variantId),
            productId: attachForm.dataset.rcbPid || '',
            quantity,
            value: attachForm.dataset.rcbValue || '',
            currency: attachForm.dataset.rcbCurrency || '',
          },
        }));

        /* The repaint is the confirmation: the accessory's row LEAVES the
           suggestions and reappears in the buy box, directly under the saree it
           rides with, carrying the quantity it went in at, its own stepper and
           its own bin. Those two lists are exact complements of one `rcb.attach`
           resolution — see `snippets/rcb-attach.liquid` — so the move is what
           rendering both regions from the server produces, and it is the same
           markup a reload would give.

           ⚠ The buy box's own confirmation panel is NOT revealed from here. It
           says THIS product is in the cart, and adding earrings does not make
           that true. See `revealAdded`. */
        if (!repaint(data.sections)) { window.location.reload(); return; }
        await announceCart();
      } catch (error) {
        attachForm.submit();
      } finally {
        attachForm.classList.remove('is-busy');
      }
    });
  });
})();
